import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { RoutesPath } from 'router/routesPaths';
import { NavigationGroup } from 'components/navigation';
import useIsResizing from 'components/layouts/hooks/useIsResizing';
import ToggleMenuButton from 'components/navigation/menu/ToggleMenuButton';

import { Link } from 'types/navigation';
import { UserRole } from 'types/auth';
import { useAuthContext } from 'domain/auth/hooks/useAuthContext';
import { useRemotes } from 'mfe/remotesContext';
import { getRemoteNavIcon } from 'mfe/remoteNavIcons';

import {
  ArrowRightLeft,
  Building2,
  Calendar,
  Folder,
  Headset,
  Megaphone,
  Shield,
  Toolbox,
  UserRound,
} from 'lucide-react';

type Props = {
  isOpen: boolean;
  onMenuLinkClick: () => void;
  setSidebarOpen: () => void;
};

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(' ');

// Sections servies par un remote mais dont le lien sidebar reste natif
// (position et icône d'origine dans topLinks) : exclues des liens de remotes.
const nativeLinkPaths = new Set<string>([RoutesPath.SEND_TO_GC, RoutesPath.TRANSACTIONS]);

function NavigationSidebar({ isOpen, onMenuLinkClick, setSidebarOpen }: Props) {
  const { t } = useTranslation('navigation');
  const { pathname } = useLocation();
  const { userRole } = useAuthContext();
  const { remotes } = useRemotes();
  const isResizing = useIsResizing();

  const isActive = useCallback((path: string) => pathname.startsWith(`/${path}`), [pathname]);

  const remoteLinks = useMemo<Link[]>(
    () =>
      remotes
        .filter(({ routePath }) => !nativeLinkPaths.has(routePath))
        .map(({ routePath, label, icon }) => {
          const Icon = getRemoteNavIcon(icon);
          return { to: routePath, text: t(label), isActive: isActive(routePath), icon: <Icon /> };
        }),
    [remotes, isActive, t],
  );

  const topLinks = useMemo<Link[]>(
    () => [
      { to: RoutesPath.SEND_TO_GC, text: t('sendToGc'), isActive: isActive(RoutesPath.SEND_TO_GC), icon: <Folder /> },
      {
        to: RoutesPath.TRANSACTIONS,
        text: t('inProgressTransactions'),
        isActive: isActive(RoutesPath.TRANSACTIONS),
        icon: <ArrowRightLeft />,
      },
      {
        to: RoutesPath.MY_CLIENTS,
        text: t('clientsGestion'),
        isActive: isActive(RoutesPath.MY_CLIENTS),
        icon: <UserRound />,
      },
      {
        to: RoutesPath.SELLING_TOOLS,
        text: t('sellingTools'),
        isActive: isActive(RoutesPath.SELLING_TOOLS),
        icon: <Toolbox />,
      },
      {
        to: RoutesPath.MY_COMPANY,
        text: t('myCompany'),
        isActive: isActive(RoutesPath.MY_COMPANY),
        icon: <Building2 />,
      },
      ...remoteLinks,
    ],
    [isActive, t, remoteLinks],
  );

  const centerLinks = useMemo<Link[]>(
    () => [
      { to: RoutesPath.CALENDAR, text: t('calendar'), isActive: isActive(RoutesPath.CALENDAR), icon: <Calendar /> },
      {
        to: RoutesPath.PROMOTIONS,
        text: t('promotions'),
        isActive: isActive(RoutesPath.PROMOTIONS),
        icon: <Megaphone />,
      },
    ],
    [isActive, t],
  );

  const adminLinks = useMemo<Link[]>(
    () =>
      userRole === UserRole.ADMIN
        ? [{ to: RoutesPath.ADMIN, text: t('admin'), isActive: isActive(RoutesPath.ADMIN), icon: <Shield /> }]
        : [],
    [isActive, t, userRole],
  );

  const bottomLinks = useMemo<Link[]>(
    () => [
      ...adminLinks,
      { to: RoutesPath.SUPPORT, text: t('contactUs'), isActive: isActive(RoutesPath.SUPPORT), icon: <Headset /> },
    ],
    [adminLinks, isActive, t],
  );

  return (
    <aside
      aria-label="Navigation"
      className={cx(
        'shrink-0 overflow-hidden bg-primary',
        'transition-[width] duration-300 ease-in-out',
        isOpen ? 'w-screen xl:w-56' : 'w-0 xl:w-20',
        isResizing && 'transition-none',
      )}
    >
      <div className="flex h-full min-h-0 w-full flex-col overflow-x-hidden overflow-y-auto">
        <NavigationGroup links={topLinks} onMenuItemClick={onMenuLinkClick} isMenuOpen={isOpen} />
        <NavigationGroup
          links={centerLinks}
          onMenuItemClick={onMenuLinkClick}
          isMenuOpen={isOpen}
          isAtBottom
          isCenterNav
        />

        <NavigationGroup links={bottomLinks} onMenuItemClick={onMenuLinkClick} isMenuOpen={isOpen} isAtBottom />

        <ToggleMenuButton setMenuOpen={setSidebarOpen} isMenuOpen={isOpen} />
      </div>
    </aside>
  );
}

export default memo(NavigationSidebar);
